import { useEffect } from 'react';
import { CashPositie, KasBeheer, OmzetOverzicht, KostenOverzicht, Resultaat, UpsellTrends, AccountantPakket } from '../components/financieel';
import { useFinancieelStore } from '../stores';

function formatMonthLabel(month: string): string {
  const parts = month.split('-').map(Number);
  const d = new Date(parts[0]!, parts[1]! - 1, 1);
  const label = d.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function shiftMonth(month: string, delta: number): string {
  const parts = month.split('-').map(Number);
  const d = new Date(parts[0]!, parts[1]! - 1 + delta, 1);
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
}

export function FinancieelPage() {
  const {
    selectedMonth, isLoading, error,
    setMonth, loadOverview, loadChecklist, loadTrends, loadBankBestanden,
  } = useFinancieelStore();

  const { completeBankAuth, loadBankConnections } = useFinancieelStore();

  useEffect(() => {
    loadOverview();
    loadChecklist();
    loadTrends(6);
    loadBankBestanden();
    loadBankConnections();

    // Handle Enable Banking callback after bank consent redirect
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (code) {
      completeBankAuth(code).then(() => {
        window.history.replaceState({}, '', '/financieel');
      });
    }
  }, [loadOverview, loadChecklist, loadTrends, loadBankBestanden, loadBankConnections, completeBankAuth]);

  return (
    <>
      <div className="flex-1 overflow-hidden flex flex-col bg-muted">
        {/* Header with month navigation */}
        <div className="px-8 py-6 border-b border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-heading font-semibold text-main">Financieel</h1>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setMonth(shiftMonth(selectedMonth, -1))}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <span className="text-sm font-medium text-main min-w-[160px] text-center">
                {formatMonthLabel(selectedMonth)}
              </span>

              <button
                onClick={() => setMonth(shiftMonth(selectedMonth, 1))}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-4">
            <div className="flex items-center gap-3 text-sm text-muted">
              <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin" />
              <span>Laden...</span>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mx-8 mt-4">
            <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm">
              <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-red-800">{error}</span>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-auto">
          <div className="p-8 space-y-6 max-w-7xl mx-auto">
            <CashPositie />
            <KasBeheer />
            <OmzetOverzicht />
            <KostenOverzicht />
            <Resultaat />
            <UpsellTrends />
            <AccountantPakket />
          </div>
        </div>
      </div>
    </>
  );
}
